'use client'

import { ENV } from '../env'
import type { AnalyticsEvent, AnalyticsUser } from './events'
import { umamiTrack } from './umami'

class Analytics {
  private user: AnalyticsUser | null = null
  private enabled: boolean

  constructor() {
    this.enabled = ENV.ANALYTICS_ENABLED
  }

  /**
   * Définir l'utilisateur courant
   */
  setUser(user: AnalyticsUser) {
    this.user = user
  }

  /**
   * Tracker un événement
   */
  track<T extends AnalyticsEvent>(event: T['name'], properties: T['properties']) {
    // Umami n'est pas conditionné par le backend analytics : il reçoit
    // l'événement même quand celui-ci est désactivé, pour garder un tunnel
    // complet. Les page views sont déjà auto-trackées par le script.
    if (event !== 'page_view') {
      umamiTrack(event, properties as Record<string, string | number | boolean>)
    }

    if (!this.enabled && ENV.IS_PRODUCTION_MODE) return

    const payload = {
      event,
      properties: {
        ...properties,
        timestamp: Date.now(),
        url: typeof window !== 'undefined' ? window.location.href : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        user: this.user,
      },
    }

    // Log en dev
    console.log(`${new Date().toISOString()} [Next.js] [Analytics]`, event, properties)

    // Envoyer au backend
    this.sendToBackend(payload)

    // Google Analytics (optionnel)
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as any).gtag('event', event, properties)
    }
  }

  /**
   * Envoyer au backend analytics
   */
  private async sendToBackend(payload: any) {
    try {
      const endpoint = ENV.ANALYTICS_ENDPOINT || '/api/analytics'

      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true, // Important pour les événements de sortie de page
      })
    } catch (error) {
      if (ENV.IS_STAGING) {
        console.error(`${new Date().toISOString()} [Next.js] ERROR Analytics error:`, error)
      }
    }
  }

  /**
   * Track page view
   */
  pageView(path: string, locale: string) {
    this.track('page_view', { path, locale })
  }
}

export const analytics = new Analytics()

// Alias pour faciliter l'import
export const trackEvent = analytics.track.bind(analytics)
export const setAnalyticsUser = analytics.setUser.bind(analytics)
export const trackPageView = analytics.pageView.bind(analytics)
